import Konva from "konva";

const FIRST_FRAME = 1;
const DEFAULT_DURATION = 0.7;
const PATH_TOUCH_PADDING = 12;
const DEFAULT_SPEED = 1;
const PLAYER_SPEEDS = [0.5, 1, 1.5, 2];
const BEZIER_SAMPLE_DISTANCE = 100;
const BEZIER_MAX_SEGMENTS = 12;
const BALL_ID = "animation-ball";
const BALL_TYPE = "ball";
const BALL_RADIUS_X = 30;
const BALL_RADIUS_Y = 18;
const BALL_HOLD_PADDING = 10;
const BEZIER_TOOLS = [
    "bezierLine",
    "bezierLineDashed",
    "bezierLineTab",
    "bezierLineTabDashed",
];

/**
 * Keyframe editor/player for whiteboard player nodes.
 * Frames contain plain data only, so they are safe to keep in Vue state.
 */
export default class Animation {
    frames = {};
    currentFrame = FIRST_FRAME;
    isPlaying = false;

    constructor(stage, layer, options = {}) {
        this.layer = layer;
        this.stage = stage;
        this.duration = options.duration || DEFAULT_DURATION;
        this.playerSpeeds = { ...(options.playerSpeeds || {}) };
        this.selectedPlayerId = options.selectedPlayerId || null;
        this.selectedPlayerLabel = "";
        this.onPlayerSelect = options.onPlayerSelect;
        this.players = new Map();
        this.playerLabels = new Map();
        this.selectionIndicators = new Map();
        this.tweens = new Set();
        this.playToken = 0;
    }

    get frameCount() {
        return Object.keys(this.frames).length;
    }

    /** Rebuild clones and merge them with any previously-created frames. */
    reinit(players = [], frames = {}, paths = []) {
        this.stop();
        this.layer.destroyChildren();
        this.players.clear();
        this.playerLabels.clear();
        this.selectionIndicators.clear();

        for (const player of players) {
            const id = player.id();
            if (!id) continue;

            if (player.attrs.instance?.name === "ShadedCross") {
                this.addShadedCrossPlayers(player, id);
                continue;
            }

            const label =
                player.attrs.instance?.text?.() || player.name() || id;

            this.addAnimationPlayer(player, id, label);
        }

        this.frames = this.normaliseFrames(frames);
        if (!this.frameCount) {
            this.frames[FIRST_FRAME] = {};
        }

        this.restoreAnimationObjectsFromFrames();
        this.syncFramesWithMainBoard();
        this.generateFramesFromPaths(paths);
        this.syncPlayerSpeeds();
        this.currentFrame = Math.min(
            Math.max(FIRST_FRAME, this.currentFrame),
            this.frameCount
        );
        this.showFrame(this.currentFrame);
        this.selectPlayer(this.selectedPlayerId, false);
        return this.frames;
    }

    addShadedCrossPlayers(player, parentId) {
        const circles = player.attrs.instance?.circles || [];
        const sourceLayer = player.getLayer();

        circles.forEach((circle, index) => {
            const id = `${parentId}-shaded-${index + 1}`;
            const transform = circle
                .getAbsoluteTransform(sourceLayer)
                .decompose();
            this.addAnimationPlayer(circle, id, `ShadedCross ${index + 1}`, {
                ...transform,
                opacity: circle.getAbsoluteOpacity(),
            });
        });
    }

    addAnimationPlayer(player, id, label, attrs = {}) {
        const clone = player.clone({
            ...attrs,
            id: `${id}-animation`,
            draggable: true,
            listening: true,
        });
        this.removePlayerClickListeners(clone);
        clone.setAttr("instance", undefined);
        clone.find(".remover").forEach((node) => node.visible(false));
        clone.on("dragstart.animation", () => this.selectPlayer(id));
        clone.on("dragmove.animation", () => {
            if (id !== BALL_ID) this.syncBallWithHolder(id);
        });
        clone.on("dragend.animation", () => {
            if (id === BALL_ID) this.updateBallHolderFromPosition();
            else this.syncBallWithHolder(id);
            this.captureCurrentFrame();
        });
        clone.on("click.animation tap.animation", () => this.selectPlayer(id));

        this.players.set(id, clone);
        this.playerLabels.set(id, label);
        this.layer.add(clone);
        this.addSelectionIndicator(id, clone);
    }

    restoreAnimationObjectsFromFrames() {
        for (let frame = FIRST_FRAME; frame <= this.frameCount; frame++) {
            const state = this.frames[frame]?.[BALL_ID];
            if (state?.animationObjectType === BALL_TYPE) {
                this.createBall(state);
                return;
            }
        }
    }

    createBall(attrs = {}) {
        const scale = this.stage.scale();
        const defaultX =
            this.stage.width() / Math.max(Math.abs(scale.x), 0.001) / 2;
        const defaultY =
            this.stage.height() / Math.max(Math.abs(scale.y), 0.001) / 2;
        const ball = new Konva.Group({
            x: defaultX,
            y: defaultY,
            animationObjectType: BALL_TYPE,
        });

        ball.add(
            new Konva.Ellipse({
                radiusX: BALL_RADIUS_X,
                radiusY: BALL_RADIUS_Y,
                fill: "#8b4513",
                stroke: "#3f1d0b",
                strokeWidth: 2,
            })
        );
        ball.add(
            new Konva.Line({
                points: [-10, 0, 10, 0],
                stroke: "#f8fafc",
                strokeWidth: 2.5,
                lineCap: "round",
                listening: false,
            })
        );

        [-6, -2, 2, 6].forEach((x) => {
            ball.add(
                new Konva.Line({
                    points: [x, -4, x, 4],
                    stroke: "#f8fafc",
                    strokeWidth: 2,
                    lineCap: "round",
                    listening: false,
                })
            );
        });

        [-1, 1].forEach((side) => {
            ball.add(
                new Konva.Line({
                    points: [side * 21, -10, side * 17, 0, side * 21, 10],
                    stroke: "#f8fafc",
                    strokeWidth: 3,
                    lineCap: "round",
                    lineJoin: "round",
                    tension: 0.25,
                    listening: false,
                })
            );
        });

        this.addAnimationPlayer(ball, BALL_ID, "Ball", attrs);
        ball.destroy();
        return this.players.get(BALL_ID);
    }

    addBall() {
        if (this.isPlaying) return false;
        if (this.players.has(BALL_ID)) {
            this.selectPlayer(BALL_ID);
            return true;
        }

        this.stop();
        this.captureCurrentFrame();
        const ball = this.createBall();
        const state = this.getPlayerState(ball);
        for (let frame = FIRST_FRAME; frame <= this.frameCount; frame++) {
            this.frames[frame] = this.frames[frame] || {};
            this.frames[frame][BALL_ID] = { ...state };
        }
        this.syncPlayerSpeeds();
        this.showFrame(this.currentFrame);
        this.selectPlayer(BALL_ID);
        return true;
    }

    hasBall() {
        return this.players.has(BALL_ID);
    }

    syncBallWithHolder(holderId) {
        const ball = this.players.get(BALL_ID);
        const holder = this.players.get(holderId);
        if (!ball || !holder || ball.getAttr("holderId") !== holderId) {
            return false;
        }

        ball.position({
            x: holder.x() + Number(ball.getAttr("holderOffsetX") || 0),
            y: holder.y() + Number(ball.getAttr("holderOffsetY") || 0),
        });
        return true;
    }

    updateBallHolderFromPosition() {
        const ball = this.players.get(BALL_ID);
        if (!ball) return null;

        const ballGeometry = this.getPlayerGeometry(ball);
        let nearest = null;
        this.players.forEach((player, id) => {
            if (id === BALL_ID || !player.isVisible()) return;
            const geometry = this.getPlayerGeometry(player);
            const distance = this.distance(
                ballGeometry.center,
                geometry.center
            );
            if (
                distance <=
                    ballGeometry.radius + geometry.radius + BALL_HOLD_PADDING &&
                (!nearest || distance < nearest.distance)
            ) {
                nearest = { id, player, distance };
            }
        });

        if (!nearest) {
            ball.setAttrs({
                holderId: null,
                holderOffsetX: 0,
                holderOffsetY: 0,
            });
            this.setBallPossessionFromFrame(null, 0, 0);
            return null;
        }

        const holderOffsetX = ball.x() - nearest.player.x();
        const holderOffsetY = ball.y() - nearest.player.y();
        ball.setAttrs({
            holderId: nearest.id,
            holderOffsetX,
            holderOffsetY,
        });
        this.setBallPossessionFromFrame(
            nearest.id,
            holderOffsetX,
            holderOffsetY
        );
        return nearest.id;
    }

    setBallPossessionFromFrame(holderId, offsetX, offsetY) {
        for (let frame = this.currentFrame; frame <= this.frameCount; frame++) {
            const ballState = this.frames[frame]?.[BALL_ID];
            if (!ballState) continue;
            ballState.holderId = holderId;
            ballState.holderOffsetX = offsetX;
            ballState.holderOffsetY = offsetY;

            const holderState = holderId
                ? this.frames[frame]?.[holderId]
                : null;
            if (holderState) {
                ballState.x = holderState.x + offsetX;
                ballState.y = holderState.y + offsetY;
            }
        }
    }

    syncPlayerSpeeds() {
        const storedSpeeds = this.playerSpeeds;
        const speedsByFrame = {};

        for (let frame = FIRST_FRAME; frame <= this.frameCount; frame++) {
            speedsByFrame[frame] = {};
            this.players.forEach((player, id) => {
                // The direct ID lookup supports speed data created before
                // speeds became frame-specific.
                const storedSpeed =
                    storedSpeeds?.[frame]?.[id] ?? storedSpeeds?.[id];
                const numericSpeed = Number(storedSpeed);
                speedsByFrame[frame][id] = PLAYER_SPEEDS.includes(numericSpeed)
                    ? numericSpeed
                    : DEFAULT_SPEED;
            });
        }
        this.playerSpeeds = speedsByFrame;

        if (!this.players.has(this.selectedPlayerId)) {
            this.selectedPlayerId = null;
            this.selectedPlayerLabel = "";
        }
    }

    removePlayerClickListeners(player) {
        const events = "click tap dblclick dbltap";
        player.off(events);
        player.find(() => true).forEach((node) => node.off(events));
    }

    addSelectionIndicator(id, player) {
        const geometry = this.getPlayerGeometry(player);
        const indicator = new Konva.Circle({
            x: geometry.offset.x,
            y: geometry.offset.y,
            radius: geometry.radius + 5,
            stroke: "#0e8ce9",
            strokeWidth: 3,
            dash: [8, 4],
            listening: false,
            visible: false,
            name: "animation-player-selection",
        });
        player.add(indicator);
        indicator.moveToBottom();
        this.selectionIndicators.set(id, indicator);
    }

    selectPlayer(id, notify = true) {
        const selectedId = this.players.has(id) ? id : null;
        this.selectedPlayerId = selectedId;
        this.selectedPlayerLabel = selectedId
            ? this.playerLabels.get(selectedId) || selectedId
            : "";

        this.selectionIndicators.forEach((indicator, playerId) => {
            indicator.visible(playerId === selectedId);
        });
        this.layer.batchDraw();

        if (notify) {
            this.onPlayerSelect?.({
                id: selectedId,
                label: this.selectedPlayerLabel,
                speed: this.getPlayerSpeed(selectedId),
            });
        }
        return selectedId;
    }

    getPlayerSpeed(id, frame = this.currentFrame) {
        return Number(this.playerSpeeds[frame]?.[id]) || DEFAULT_SPEED;
    }

    setPlayerSpeed(
        speed,
        id = this.selectedPlayerId,
        frame = this.currentFrame
    ) {
        const numericSpeed = Number(speed);
        if (
            !id ||
            !this.players.has(id) ||
            !PLAYER_SPEEDS.includes(numericSpeed)
        ) {
            return false;
        }
        if (!this.playerSpeeds[frame]) this.playerSpeeds[frame] = {};
        this.playerSpeeds[frame][id] = numericSpeed;
        this.onPlayerSelect?.({
            id,
            label: this.playerLabels.get(id) || id,
            speed: numericSpeed,
        });
        return true;
    }

    normaliseFrames(frames) {
        if (!frames || typeof frames !== "object") return {};

        const entries = Array.isArray(frames)
            ? frames.map((frame, index) => [index + 1, frame])
            : Object.entries(frames)
                  .filter(([key]) => Number(key) > 0)
                  .sort(([a], [b]) => Number(a) - Number(b));

        return entries.reduce((result, [, frame], index) => {
            result[index + 1] = this.cloneFrame(frame);
            return result;
        }, {});
    }

    cloneFrame(frame) {
        if (!frame || typeof frame !== "object") return {};
        return Object.entries(frame).reduce((copy, [id, state]) => {
            if (state && typeof state === "object") copy[id] = { ...state };
            return copy;
        }, {});
    }

    snapshotPlayers() {
        const ball = this.players.get(BALL_ID);
        if (ball) {
            ball.setAttrs({
                isPassing: false,
                passProgress: 0,
                passDistance: 0,
            });
            if (ball.getAttr("holderId")) {
                this.syncBallWithHolder(ball.getAttr("holderId"));
            }
        }
        const frame = {};
        this.players.forEach((player, id) => {
            frame[id] = this.getPlayerState(player);
        });
        return frame;
    }

    getPlayerState(player) {
        const state = {
            x: player.x(),
            y: player.y(),
            rotation: player.rotation(),
            scaleX: player.scaleX(),
            scaleY: player.scaleY(),
            opacity: player.opacity(),
        };
        const animationObjectType = player.getAttr("animationObjectType");
        if (animationObjectType) {
            state.animationObjectType = animationObjectType;
        }
        if (animationObjectType === BALL_TYPE) {
            state.holderId = player.getAttr("holderId") || null;
            state.holderOffsetX = Number(player.getAttr("holderOffsetX") || 0);
            state.holderOffsetY = Number(player.getAttr("holderOffsetY") || 0);
        }
        return state;
    }

    syncFramesWithMainBoard() {
        const mainBoardFrame = this.snapshotPlayers();

        // Players no longer present on the main board must not survive in
        // stored keyframes, even though those frames are otherwise retained.
        for (let index = FIRST_FRAME; index <= this.frameCount; index++) {
            const frame = this.frames[index] || {};
            Object.keys(frame).forEach((id) => {
                if (!this.players.has(id)) delete frame[id];
            });
            if (
                frame[BALL_ID]?.holderId &&
                !this.players.has(frame[BALL_ID].holderId)
            ) {
                frame[BALL_ID].holderId = null;
                frame[BALL_ID].holderOffsetX = 0;
                frame[BALL_ID].holderOffsetY = 0;
            }
            this.frames[index] = frame;
        }

        // Frame 1 is always the source-of-truth snapshot of the main board.
        this.frames[FIRST_FRAME] = mainBoardFrame;

        // Existing later keyframes keep their absolute positions. New players
        // inherit their preceding position until explicitly moved in a frame.
        let previous = mainBoardFrame;
        for (let index = FIRST_FRAME + 1; index <= this.frameCount; index++) {
            const frame = this.frames[index] || {};
            this.players.forEach((player, id) => {
                frame[id] = {
                    ...(previous[id] || this.getPlayerState(player)),
                    ...(frame[id] || {}),
                };
            });
            this.frames[index] = frame;
            previous = frame;
        }
    }

    generateFramesFromPaths(paths = []) {
        const routes = this.matchPathsToPlayers(paths);
        if (!routes.size) return this.frames;

        const hasLeadingSquigglyRoute = Array.from(routes.values()).some(
            ({ isSquiggly }) => isSquiggly
        );
        routes.forEach(({ isSquiggly, isSquigglyFirst }, id) => {
            if (!isSquiggly) return;
            this.setGeneratedSpeedDefault(FIRST_FRAME, id, 0.5);
            if (!isSquigglyFirst) {
                this.setGeneratedSpeedDefault(FIRST_FRAME + 1, id, 0.5);
            }
        });
        const longestRoute = Math.max(
            ...Array.from(
                routes.values(),
                ({ points, isSquiggly }) =>
                    points.length +
                    (hasLeadingSquigglyRoute && !isSquiggly ? 1 : 0)
            )
        );
        const requiredFrames = Math.max(
            this.frameCount,
            FIRST_FRAME + longestRoute
        );

        // Extend the timeline without disturbing existing player keyframes.
        for (
            let index = this.frameCount + 1;
            index <= requiredFrames;
            index++
        ) {
            this.frames[index] = this.cloneFrame(this.frames[index - 1]);
        }

        for (let index = FIRST_FRAME + 1; index <= requiredFrames; index++) {
            const previous = this.frames[index - 1];
            const frame = this.frames[index] || this.cloneFrame(previous);

            routes.forEach(({ points, offset, isSquiggly }, id) => {
                const delay = hasLeadingSquigglyRoute && !isSquiggly ? 1 : 0;
                const routeIndex = index - 2 - delay;
                const previousState =
                    previous[id] || this.frames[FIRST_FRAME][id];
                const currentState = frame[id] || previousState;
                const point =
                    routeIndex < 0
                        ? null
                        : points[Math.min(routeIndex, points.length - 1)];
                frame[id] = {
                    ...previousState,
                    ...currentState,
                    x: point
                        ? point.x - offset.x
                        : this.frames[FIRST_FRAME][id].x,
                    y: point
                        ? point.y - offset.y
                        : this.frames[FIRST_FRAME][id].y,
                };
            });

            this.frames[index] = frame;
        }

        return this.frames;
    }

    matchPathsToPlayers(paths) {
        const routes = new Map();

        for (const path of paths || []) {
            const points = this.getPathPoints(path);
            if (points.length < 2) continue;

            let match = null;
            this.players.forEach((player, id) => {
                const geometry = this.getPlayerGeometry(player);
                const firstDistance = this.distance(points[0], geometry.center);
                const lastDistance = this.distance(
                    points[points.length - 1],
                    geometry.center
                );
                const distance = Math.min(firstDistance, lastDistance);

                if (
                    distance <= geometry.radius + PATH_TOUCH_PADDING &&
                    (!match || distance < match.distance)
                ) {
                    match = {
                        id,
                        distance,
                        reverse: lastDistance < firstDistance,
                        offset: geometry.offset,
                    };
                }
            });

            if (!match) continue;
            const orientedPoints = match.reverse
                ? [...points].reverse()
                : points;
            const pathName = path.attrs.instance?.name;
            const isSquigglyFirst = [
                "penSquigglyFirstArrow",
                "penSquigglyFirstTab",
            ].includes(pathName);
            const isSquiggly =
                isSquigglyFirst ||
                ["penSquigglyArrow", "penSquigglyTab"].includes(pathName);
            const routePoints =
                isSquiggly && !isSquigglyFirst
                    ? [
                          this.getPointOnPath(orientedPoints, 0.5),
                          orientedPoints[orientedPoints.length - 1],
                      ]
                    : orientedPoints.slice(1);
            if (!routePoints.length) continue;

            // When more than one path touches a player, use the path whose
            // endpoint is closest to that player to keep matching deterministic.
            const current = routes.get(match.id);
            if (!current || match.distance < current.distance) {
                routes.set(match.id, {
                    distance: match.distance,
                    isSquiggly,
                    isSquigglyFirst,
                    offset: match.offset,
                    points: routePoints,
                });
            }
        }

        return routes;
    }

    setGeneratedSpeedDefault(frame, id, speed) {
        const storedSpeed =
            this.playerSpeeds?.[frame]?.[id] ?? this.playerSpeeds?.[id];
        if (PLAYER_SPEEDS.includes(Number(storedSpeed))) return;
        if (
            !this.playerSpeeds[frame] ||
            typeof this.playerSpeeds[frame] !== "object"
        ) {
            this.playerSpeeds[frame] = {};
        }
        this.playerSpeeds[frame][id] = speed;
    }

    getPointOnPath(points, progress) {
        const segments = [];
        let totalLength = 0;

        for (let index = 1; index < points.length; index++) {
            const length = this.distance(points[index - 1], points[index]);
            segments.push(length);
            totalLength += length;
        }

        if (!totalLength) return { ...points[points.length - 1] };
        const targetLength = totalLength * progress;
        let traversed = 0;

        for (let index = 0; index < segments.length; index++) {
            const segmentLength = segments[index];
            if (traversed + segmentLength >= targetLength) {
                const start = points[index];
                const end = points[index + 1];
                const ratio = (targetLength - traversed) / segmentLength;
                return {
                    x: start.x + (end.x - start.x) * ratio,
                    y: start.y + (end.y - start.y) * ratio,
                };
            }
            traversed += segmentLength;
        }

        return { ...points[points.length - 1] };
    }

    getPathPoints(path) {
        const instance = path?.attrs?.instance;
        if (BEZIER_TOOLS.includes(instance?.name)) {
            return this.getBezierPathPoints(path, instance);
        }
        if (!instance?.toRelativePoints) return [];

        const position = path.position();
        return instance.toRelativePoints().map((point) => ({
            x: point.x + position.x,
            y: point.y + position.y,
        }));
    }

    getBezierPathPoints(path, instance) {
        const position = path.position();
        const controlPoints = [
            instance.start,
            instance.control1,
            instance.control2,
            instance.end,
        ];
        if (controlPoints.some((point) => !point)) return [];

        const [start, control1, control2, end] = controlPoints.map((point) => ({
            x: point.x() + position.x,
            y: point.y() + position.y,
        }));
        const curvePoint = (progress) => {
            const inverse = 1 - progress;
            return {
                x:
                    inverse ** 3 * start.x +
                    3 * inverse ** 2 * progress * control1.x +
                    3 * inverse * progress ** 2 * control2.x +
                    progress ** 3 * end.x,
                y:
                    inverse ** 3 * start.y +
                    3 * inverse ** 2 * progress * control1.y +
                    3 * inverse * progress ** 2 * control2.y +
                    progress ** 3 * end.y,
            };
        };

        let approximateLength = 0;
        let previous = start;
        for (let sample = 1; sample <= 20; sample++) {
            const current = curvePoint(sample / 20);
            approximateLength += this.distance(previous, current);
            previous = current;
        }

        const segments = Math.min(
            BEZIER_MAX_SEGMENTS,
            Math.max(2, Math.ceil(approximateLength / BEZIER_SAMPLE_DISTANCE))
        );
        return Array.from({ length: segments + 1 }, (value, index) =>
            curvePoint(index / segments)
        );
    }

    getPlayerGeometry(player) {
        const box = player.getClientRect({
            relativeTo: this.layer,
            skipShadow: true,
        });
        const center = {
            x: box.x + box.width / 2,
            y: box.y + box.height / 2,
        };

        return {
            center,
            offset: {
                x: center.x - player.x(),
                y: center.y - player.y(),
            },
            radius: Math.max(box.width, box.height) / 2,
        };
    }

    distance(a, b) {
        return Math.hypot(a.x - b.x, a.y - b.y);
    }

    captureCurrentFrame() {
        if (this.isPlaying || !this.frames[this.currentFrame])
            return this.frames;
        this.frames[this.currentFrame] = this.snapshotPlayers();
        return this.frames;
    }

    addFrame() {
        this.stop();
        this.captureCurrentFrame();
        const insertAt = this.currentFrame + 1;
        const previousFrameCount = this.frameCount;
        for (let index = previousFrameCount; index >= insertAt; index--) {
            this.frames[index + 1] = this.frames[index];
            this.playerSpeeds[index + 1] = this.playerSpeeds[index];
        }
        this.frames[insertAt] = this.snapshotPlayers();
        this.playerSpeeds[insertAt] = {
            ...(this.playerSpeeds[this.currentFrame] || {}),
        };
        this.syncPlayerSpeeds();
        this.showFrame(insertAt);
        return this.frames;
    }

    deleteFrame(index = this.currentFrame) {
        this.stop();
        if (this.frameCount <= FIRST_FRAME) return this.frames;
        const target = this.clampFrame(index);
        const previousFrameCount = this.frameCount;
        for (let current = target; current < previousFrameCount; current++) {
            this.frames[current] = this.frames[current + 1];
            this.playerSpeeds[current] = this.playerSpeeds[current + 1];
        }
        delete this.frames[previousFrameCount];
        delete this.playerSpeeds[previousFrameCount];
        this.syncPlayerSpeeds();
        this.showFrame(Math.min(target, this.frameCount));
        return this.frames;
    }

    previousFrame() {
        return this.showFrame(this.currentFrame - 1);
    }

    nextFrame() {
        return this.showFrame(this.currentFrame + 1);
    }

    showFrame(index) {
        this.stop();
        this.currentFrame = this.clampFrame(index);
        this.applyFrame(this.currentFrame);
        this.layer.batchDraw();
        return this.currentFrame;
    }

    clampFrame(index) {
        const numericIndex = Number(index) || FIRST_FRAME;
        return Math.min(
            Math.max(FIRST_FRAME, numericIndex),
            this.frameCount || 1
        );
    }

    applyFrame(index) {
        const frame = this.frames[index] || {};
        this.players.forEach((player, id) => {
            if (frame[id]) player.setAttrs(frame[id]);
        });
        const ball = this.players.get(BALL_ID);
        if (ball) {
            ball.setAttrs({
                isPassing: false,
                passProgress: 0,
                passDistance: 0,
            });
            if (ball.getAttr("holderId")) {
                this.syncBallWithHolder(ball.getAttr("holderId"));
            }
        }
    }

    setPlayersDraggable(draggable) {
        this.players.forEach((player) => player.draggable(draggable));
    }

    async play(callbacks = {}) {
        if (this.isPlaying || this.frameCount < 2) return false;

        this.captureCurrentFrame();
        const token = ++this.playToken;
        this.isPlaying = true;
        this.setPlayersDraggable(false);
        callbacks.onStateChange?.(true);

        if (this.currentFrame >= this.frameCount) {
            this.currentFrame = FIRST_FRAME;
            this.applyFrame(FIRST_FRAME);
            this.layer.batchDraw();
            callbacks.onFrame?.(this.currentFrame);
        }

        try {
            while (this.isPlaying && this.currentFrame < this.frameCount) {
                const next = this.currentFrame + 1;
                const completed = await this.tweenToFrame(next, token);
                if (!completed || token !== this.playToken) return false;
                this.currentFrame = next;
                callbacks.onFrame?.(next);
            }
            return true;
        } finally {
            if (token === this.playToken) {
                this.isPlaying = false;
                this.setPlayersDraggable(true);
                callbacks.onStateChange?.(false);
                callbacks.onComplete?.(this.currentFrame);
            }
        }
    }

    tweenToFrame(index, token) {
        const frame = this.frames[index] || {};
        const jobs = [];
        this.players.forEach((player, id) => {
            if (!frame[id]) return;
            const targetState = { ...frame[id] };
            delete targetState.animationObjectType;
            delete targetState.holderId;
            delete targetState.holderOffsetX;
            delete targetState.holderOffsetY;
            let speedId = id;
            if (
                id === BALL_ID &&
                frame[id].holderId &&
                frame[frame[id].holderId]
            ) {
                const holderId = frame[id].holderId;
                const holderState = frame[holderId];
                targetState.x =
                    holderState.x + Number(frame[id].holderOffsetX || 0);
                targetState.y =
                    holderState.y + Number(frame[id].holderOffsetY || 0);
                speedId = holderId;
            }
            const movementDistance = Math.hypot(
                Number(targetState.x || 0) - player.x(),
                Number(targetState.y || 0) - player.y()
            );
            const sourceHolderId = player.getAttr("holderId") || null;
            const targetHolderId = frame[id].holderId || null;
            const isPassing =
                id === BALL_ID &&
                movementDistance > 1 &&
                (sourceHolderId !== targetHolderId || !targetHolderId);
            if (id === BALL_ID) {
                player.setAttrs({
                    isPassing,
                    passProgress: 0,
                    passDistance: movementDistance,
                });
                targetState.passProgress = isPassing ? 1 : 0;
            }
            jobs.push(
                new Promise((resolve) => {
                    let tween;
                    let job;
                    tween = new Konva.Tween({
                        node: player,
                        ...targetState,
                        duration:
                            this.duration /
                            this.getPlayerSpeed(speedId, index - 1),
                        easing: Konva.Easings.Linear,
                        onFinish: () => {
                            if (id === BALL_ID) {
                                player.setAttrs({
                                    holderId: frame[id].holderId || null,
                                    holderOffsetX: Number(
                                        frame[id].holderOffsetX || 0
                                    ),
                                    holderOffsetY: Number(
                                        frame[id].holderOffsetY || 0
                                    ),
                                    isPassing: false,
                                    passProgress: 0,
                                    passDistance: 0,
                                });
                            }
                            this.tweens.delete(job);
                            tween.destroy();
                            resolve(token === this.playToken);
                        },
                    });
                    job = { tween, resolve };
                    this.tweens.add(job);
                    tween.play();
                })
            );
        });
        if (!jobs.length) return Promise.resolve(true);
        return Promise.all(jobs).then((results) => results.every(Boolean));
    }

    stop() {
        const wasPlaying = this.isPlaying;
        this.playToken++;
        this.isPlaying = false;
        this.tweens.forEach(({ tween, resolve }) => {
            tween.pause();
            tween.destroy();
            resolve(false);
        });
        this.tweens.clear();
        if (wasPlaying) {
            this.applyFrame(this.currentFrame);
            this.layer.batchDraw();
        }
        this.setPlayersDraggable(true);
    }

    destroy() {
        this.stop();
        this.players.clear();
        this.playerLabels.clear();
        this.selectionIndicators.clear();
        this.layer.destroyChildren();
        this.layer.batchDraw();
    }
}
